import { Fragment, useCallback, useMemo, useState } from "react"
import { ExpandLess, ExpandMore, FolderOutlined, InsertDriveFileOutlined } from "@mui/icons-material"
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import type { SkillFileEntry } from "resources/skills"

const ICON_SX = { minWidth: 32 } satisfies SxProps<Theme>
const NESTED_SX = { pl: 2 } satisfies SxProps<Theme>

export interface FileTreeLeaf {
  path: string
  label: string
  isText: boolean
}

export interface FileTreeFolder {
  name: string
  files: FileTreeLeaf[]
}

export interface FileTree {
  rootFiles: FileTreeLeaf[]
  folders: FileTreeFolder[]
}

function byLabel(left: FileTreeLeaf, right: FileTreeLeaf): number {
  return left.label.localeCompare(right.label)
}

export function buildFileTree(files: SkillFileEntry[]): FileTree {
  const rootFiles: FileTreeLeaf[] = []
  const folderFiles = new Map<string, FileTreeLeaf[]>()
  for (const file of files) {
    const slash = file.path.indexOf("/")
    if (slash === -1) {
      rootFiles.push({ path: file.path, label: file.path, isText: file.isText })
      continue
    }
    const name = file.path.slice(0, slash)
    const leaves = folderFiles.get(name) ?? []
    leaves.push({ path: file.path, label: file.path.slice(slash + 1), isText: file.isText })
    folderFiles.set(name, leaves)
  }
  const folders = [...folderFiles.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([name, leaves]) => ({ name, files: leaves.sort(byLabel) }))
  return { rootFiles: rootFiles.sort(byLabel), folders }
}

interface FileRowProps {
  leaf: FileTreeLeaf
  isSelected: boolean
  onSelect: (path: string) => void
}

function FileRow({ leaf, isSelected, onSelect }: FileRowProps) {
  const select = useCallback(() => onSelect(leaf.path), [onSelect, leaf.path])
  return (
    <ListItemButton dense selected={isSelected} aria-current={isSelected ? "true" : undefined} onClick={select}>
      <ListItemIcon sx={ICON_SX}><InsertDriveFileOutlined fontSize="small" /></ListItemIcon>
      <ListItemText primary={leaf.label} />
    </ListItemButton>
  )
}

interface FolderRowProps {
  folder: FileTreeFolder
  isOpen: boolean
  selectedPath: string | null
  onToggle: (name: string) => void
  onSelect: (path: string) => void
}

function FolderRow({ folder, isOpen, selectedPath, onToggle, onSelect }: FolderRowProps) {
  const toggle = useCallback(() => onToggle(folder.name), [onToggle, folder.name])
  return (
    <Fragment>
      <ListItemButton dense onClick={toggle} aria-expanded={isOpen}>
        <ListItemIcon sx={ICON_SX}><FolderOutlined fontSize="small" /></ListItemIcon>
        <ListItemText primary={folder.name} />
        {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </ListItemButton>
      <Collapse in={isOpen} unmountOnExit>
        <List dense disablePadding sx={NESTED_SX}>
          {folder.files.map((leaf) => <FileRow key={leaf.path} leaf={leaf} isSelected={leaf.path === selectedPath} onSelect={onSelect} />)}
        </List>
      </Collapse>
    </Fragment>
  )
}

export interface SkillFileTreeProps {
  files: SkillFileEntry[]
  selectedPath: string | null
  onSelect: (path: string) => void
}

export function SkillFileTree({ files, selectedPath, onSelect }: SkillFileTreeProps) {
  const tree = useMemo(() => buildFileTree(files), [files])
  const [openFolders, setOpenFolders] = useState<readonly string[]>([])
  const toggleFolder = useCallback((name: string) => {
    setOpenFolders((open) => (open.includes(name) ? open.filter((entry) => entry !== name) : [...open, name]))
  }, [])

  return (
    <List dense disablePadding aria-label="Files" data-testid="kelly-skill-file-tree">
      {tree.rootFiles.map((leaf) => <FileRow key={`file:${leaf.path}`} leaf={leaf} isSelected={leaf.path === selectedPath} onSelect={onSelect} />)}
      {tree.folders.map((folder) => (
        <FolderRow
          key={`folder:${folder.name}`}
          folder={folder}
          isOpen={openFolders.includes(folder.name)}
          selectedPath={selectedPath}
          onToggle={toggleFolder}
          onSelect={onSelect}
        />
      ))}
    </List>
  )
}
